import React, { useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface Accommodation {
  id: string;
  placeCode: string;
  placeName: string;
  placeType: string;
}

interface AuditResult {
  placeCode: string;
  placeName: string;
  placeType: string;
  hasPhoto: boolean;
  photoUrl?: string;
}

const PhotoAudit: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<AuditResult[]>([]);
  const [error, setError] = useState('');
  const [showOnlyMissing, setShowOnlyMissing] = useState(true);

  const checkPhotoExists = async (placeCode: string): Promise<boolean> => {
    const upperCode = placeCode.toUpperCase();
    const photoUrl = `https://firebasestorage.googleapis.com/v0/b/berwickregistration.firebasestorage.app/o/Images%2F${encodeURIComponent(upperCode)}%2F${encodeURIComponent(upperCode)}.jpg?alt=media`;
    
    try {
      const response = await fetch(photoUrl, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  const runAudit = async () => {
    setLoading(true);
    setError('');
    setResults([]);

    try {
      // Load inventory
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const docSnap = await getDoc(inventoryRef);
      
      if (!docSnap.exists()) {
        setError('No inventory data found');
        setLoading(false);
        return;
      }

      const data = docSnap.data();
      const allAccommodations: Accommodation[] = [];

      // Collect all accommodations from all types
      const types = ['Cottage', 'Apartment', 'dormA', 'dormB', 'Fireside', 'LowerCourt', 'UpperCourt', 'privateCottage'];
      
      for (const type of types) {
        if (Array.isArray(data[type])) {
          for (const acc of data[type]) {
            allAccommodations.push({
              id: acc.id,
              placeCode: acc.placeCode,
              placeName: acc.placeName || acc.placeCode,
              placeType: type
            });
          }
        }
      }

      // Check each accommodation for a photo
      const auditResults: AuditResult[] = [];
      
      for (const acc of allAccommodations) {
        const hasPhoto = await checkPhotoExists(acc.placeCode);
        auditResults.push({
          placeCode: acc.placeCode,
          placeName: acc.placeName,
          placeType: acc.placeType,
          hasPhoto,
          photoUrl: hasPhoto 
            ? `https://firebasestorage.googleapis.com/v0/b/berwickregistration.firebasestorage.app/o/Images%2F${encodeURIComponent(acc.placeCode.toUpperCase())}%2F${encodeURIComponent(acc.placeCode.toUpperCase())}.jpg?alt=media`
            : undefined
        });
      }

      // Sort results: missing photos first, then by placeCode
      auditResults.sort((a, b) => {
        if (a.hasPhoto === b.hasPhoto) {
          return a.placeCode.localeCompare(b.placeCode);
        }
        return a.hasPhoto ? 1 : -1;
      });

      setResults(auditResults);
    } catch (err) {
      console.error('Error running photo audit:', err);
      setError(err instanceof Error ? err.message : 'Failed to run audit');
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = showOnlyMissing 
    ? results.filter(r => !r.hasPhoto)
    : results;

  const missingCount = results.filter(r => !r.hasPhoto).length;
  const totalCount = results.length;

  return (
    <section>
      <div className="action-section">
        <div className="action-header">
          <h3>Photo Audit</h3>
          <p>Check which accommodations are missing photos in Firebase Storage.</p>
        </div>
        
        <button
          onClick={runAudit}
          disabled={loading}
          className="action-button"
        >
          {loading ? 'Checking...' : 'Run Photo Audit'}
        </button>

        {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}

        {results.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem',
              padding: '1rem',
              background: missingCount > 0 ? '#fff3cd' : '#d4edda',
              borderRadius: '8px'
            }}>
              <div>
                <strong>Results:</strong> {missingCount} of {totalCount} units missing photos
                {missingCount === 0 && ' ✓'}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showOnlyMissing}
                  onChange={(e) => setShowOnlyMissing(e.target.checked)}
                />
                Show only missing
              </label>
            </div>

            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '8px'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Place Code</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Photo</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result) => (
                    <tr key={result.placeCode} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>{result.placeCode}</td>
                      <td style={{ padding: '0.75rem' }}>{result.placeName}</td>
                      <td style={{ padding: '0.75rem' }}>{result.placeType}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {result.hasPhoto ? (
                          <span style={{ color: '#28a745' }}>✓</span>
                        ) : (
                          <span style={{ color: '#dc3545' }}>✗ Missing</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PhotoAudit;

